import React, { useState } from 'react';
import classnames from 'classnames';
import * as Sift from '@sift/types';
import { isLoading } from 'resift';
import _get from 'lodash/get';
import * as Types from 'pages/AttributesEdit/typings';

// Components
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Switch from '@material-ui/core/Switch';
import Typography from '@material-ui/core/Typography';
import LoadingButton from 'atoms/LoadingButton';

// Style
import makeStyles from '@material-ui/core/styles/makeStyles';

interface Props {
  classes?: any;
  field: Sift.Field;
  status: number;
  values: Types.AttributesEditFormValues;
  deleteCategoryField: (field: Sift.Field) => void;
  onChange: (field: string, value: any) => void;
}

function DeleteAttribute({
  classes: classesFromProps,
  field,
  status,
  values,
  deleteCategoryField,
  onChange,
}: Props) {
  const _classes = useStyles();
  const classes = { ...classesFromProps, ..._classes };
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogInput, setDialogInput] = useState('');
  const enabled = !_get(values, 'client.connectDisabled');
  const shouldRenderDeleteButton =
    _get(values, 'client.editable') && _get(values, 'client.deletable');

  const handleDialogChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDialogInput(e.target.value);
  };

  const toggleDialog = (deleteAttribute: boolean) => {
    if (deleteAttribute) {
      deleteCategoryField(field);
    } else {
      setDialogInput('');
      setDialogOpen(!dialogOpen);
    }
  };

  const handleChangeEnabled = (e: React.ChangeEvent<HTMLInputElement>, isChecked: boolean) => {
    onChange('client.connectDisabled', !isChecked);
  };

  const buttonClass = classnames({
    [classes.red]: dialogInput === field.displayName,
    [classes.disabled]: dialogInput !== field.displayName,
  });

  return (
    <Card className={classes.root}>
      <CardContent>
        <div className={classes.content}>
          <div>
            <Typography variant="h6">Enable Attribute</Typography>
            <Typography variant="subtitle2">
              If switched off, the attribute will not appear anywhere in Sift. This would usually be
              used to hide something existing.
            </Typography>
          </div>
          <Switch
            id="disable-switch"
            inputProps={{ 'aria-label': 'disable switch' }}
            checked={enabled}
            onChange={handleChangeEnabled}
          />
        </div>
        {shouldRenderDeleteButton && (
          <div className={classes.marginTop}>
            <Typography variant="h6">Delete Attribute</Typography>
            <Typography variant="subtitle2">
              Delete this attribute from the Sift attribute list? You cannot undo this action, and
              any source mappings will also be deleted.
            </Typography>
            <Button
              classes={{ root: classes.openDialogButton }}
              disableElevation={true}
              onClick={() => {
                toggleDialog(false);
              }}
              variant="contained"
            >
              Delete Attribute
            </Button>
          </div>
        )}
      </CardContent>
      <Dialog
        open={dialogOpen}
        onClose={() => {
          toggleDialog(false);
        }}
      >
        <DialogTitle>Are you sure you want to delete "{field.displayName}"?</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2">
            To delete this field, type out{' '}
            <strong className={classes.red}>"{field.displayName}" </strong>
            in the input below to enable the delete button.
            <br />
            <br />
            WARNING: This may impact your mappings, and{' '}
            <strong>you cannot undo this action! </strong>
          </Typography>
          <TextField
            label={`Type ${field.displayName}`}
            className={classes.textField}
            value={dialogInput}
            inputProps={{ 'aria-label': 'attribute name input' }}
            onChange={handleDialogChange}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              toggleDialog(false);
            }}
          >
            Cancel
          </Button>
          <LoadingButton
            className={buttonClass}
            label="Delete"
            loading={isLoading(status)}
            variant="text"
            onClick={() => {
              toggleDialog(true);
            }}
            disabled={dialogInput !== field.displayName}
          />
        </DialogActions>
      </Dialog>
    </Card>
  );
}

const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: theme.spacing(2),
    maxWidth: 900,
  },
  openDialogButton: {
    color: theme.palette.common.white,
    background: theme.palette.error.main,
    marginTop: theme.spacing(),
  },
  textField: {
    width: 400,
  },
  red: {
    color: theme.palette.error.main,
  },
  disabled: {
    cursor: 'not-allowed',
  },
  content: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  marginTop: {
    marginTop: theme.spacing(2),
  },
}));

export default DeleteAttribute;
